import { type Dispatch, type SetStateAction, useEffect } from "react";

import { Button, Span } from "@icg360/design-system";

import { ENROLLMENT_STATUS, PAYMENT_METHOD_CODE } from "consts";
import { type Policy } from "data/keystone-api-mapping";
import { usePolicyDetail } from "data/ss-data-hooks";
import EditBillingEmail from "pages/settings/common/edit-billing-email";
import SettingsPolicy, {
  type SettingsPolicyDetailItem,
} from "pages/settings/common/settings-policy";
import {
  type PolicyDetail,
  formatAddress,
  getMortgageeClass,
  getPaymentMethodDetails,
  isEasyPayEligible,
  isEasyPayEnrolled,
  switchPolicy,
} from "utils";

import EditMortgagee from "./edit-mortgagee";
import styles from "./payment-item.module.scss";

const getMortgageeItems = (
  mortgagees: NonNullable<PolicyDetail["userBilling"]>["mortgagees"],
  policyId: string,
  keystonePolicyId: string,
): SettingsPolicyDetailItem[] => {
  let detailsItems: SettingsPolicyDetailItem[] = [];
  mortgagees?.forEach((mortgagee) => {
    const { name, loanNumber, index, ...rest } = mortgagee;
    detailsItems = detailsItems.concat([
      {
        label: `${getMortgageeClass(index)} mortgagee:`,
        value: (
          <>
            {name}
            <EditMortgagee
              mortgagee={mortgagee}
              keystonePolicyId={keystonePolicyId}
            />
          </>
        ),
      },
      {
        label: "Billing address:",
        value: formatAddress(rest),
      },
      {
        label: "Loan number:",
        value: loanNumber,
      },
    ]);
  });
  return detailsItems;
};

const getDirectOrEasyPayItems = (
  policy: Policy,
  paymentFrequency: string,
  userBillingAddress: string,
): SettingsPolicyDetailItem[] => {
  return [
    { label: "Payment frequency:", value: paymentFrequency },
    {
      label: "Billing email:",
      value: (
        <>
          {userBillingAddress ||
            "No billing email address associated with this policy"}
          {userBillingAddress && (
            <EditBillingEmail policyId={policy?.policyId ?? ""} />
          )}
        </>
      ),
    },
  ];
};

const getDetailItems = (
  policy: Policy,
  userDetails?: PolicyDetail["userDetails"],
  userBilling?: PolicyDetail["userBilling"],
  userBillingAddress?: PolicyDetail["userBillingAddress"],
): SettingsPolicyDetailItem[] => {
  let detailsItems: SettingsPolicyDetailItem[] = [
    { label: "Policy number:", value: policy.policyId },
  ];

  if (!userDetails || !userBilling) {
    return detailsItems;
  }

  const { keystonePolicyId, insuredPreferences } = userDetails;

  const { accounting, mortgagees } = userBilling;

  const { paymentMethod, paymentMethodLabel, paymentFrequencyLabel } =
    getPaymentMethodDetails(accounting?.paymentPlan?.planType?.toLowerCase());

  const easyPayEnrolled = isEasyPayEnrolled(userBilling);
  const isMortgagee = paymentMethod === PAYMENT_METHOD_CODE.INVOICE;

  const pendingEasyPayEnrollment =
    !easyPayEnrolled &&
    insuredPreferences?.easyPayEnrollmentStatus === ENROLLMENT_STATUS.PENDING;
  const showEditPaymentMethod = !isMortgagee && !pendingEasyPayEnrollment;

  detailsItems.push({
    label: "Payment method:",
    value: (
      <>
        {paymentMethodLabel}
        {showEditPaymentMethod && <EditPaymentButton policy={policy} />}
      </>
    ),
  });

  detailsItems = detailsItems.concat(
    isMortgagee
      ? getMortgageeItems(mortgagees, policy.policyId, keystonePolicyId ?? "")
      : getDirectOrEasyPayItems(
          policy,
          paymentFrequencyLabel,
          userBillingAddress ?? "",
        ),
  );

  return detailsItems;
};

type PaymentItemProps = {
  policy: Policy;
  setEasyPayBanner: Dispatch<SetStateAction<boolean>>;
};

const PaymentItem = ({ policy, setEasyPayBanner }: PaymentItemProps) => {
  const { data: PolicyPaymentData, isLoading } = usePolicyDetail(
    policy?.policyId ?? "",
  );
  const { userDetails, userBilling, userBillingAddress } =
    PolicyPaymentData ?? {};
  const { propertyAddress, policyId, isCommercial } = policy;

  useEffect(() => {
    if (
      userBilling &&
      userDetails &&
      isEasyPayEligible(userBilling, userDetails)
    ) {
      setEasyPayBanner(true);
    }
  }, [setEasyPayBanner, isLoading, userBilling, userDetails]);

  const detailItems =
    !isLoading && policyId
      ? getDetailItems(policy, userDetails, userBilling, userBillingAddress)
      : [];

  return (
    <SettingsPolicy
      isCommercial={!!isCommercial}
      address={propertyAddress}
      detailItems={detailItems}
      loading={isLoading || !policyId}
    >
      {!userDetails || !userBilling ? (
        <Span>An error occured while fetching this data</Span>
      ) : null}

      {isEasyPayEligible(userBilling, userDetails) ? (
        <EnrollInEasyPayButton policy={policy} />
      ) : null}
    </SettingsPolicy>
  );
};

export default PaymentItem;

const EditPaymentButton = ({ policy }: { policy: Policy }) => {
  return (
    <Button
      appearance="tertiary"
      size="xs"
      onPress={() => {
        switchPolicy(
          policy.insightPolicyId,
          "/my/billing/update?location=settings",
        );
      }}
      data-testid="payment-method-edit-button"
    >
      Edit
    </Button>
  );
};

const EnrollInEasyPayButton = ({ policy }: { policy: Policy }) => {
  return (
    <div className={styles.easyPayLink}>
      <Button
        appearance="secondary"
        leadingIcon="Money"
        onPress={() => {
          switchPolicy(
            policy.insightPolicyId,
            "/my/billing/update?location=settings",
          );
        }}
        data-testid="enroll-in-easypay-button"
      >
        Enroll in EasyPay
      </Button>
    </div>
  );
};
